import { ApolloClient, InMemoryCache, from, Observable } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import { createHttpLink } from 'apollo-link-http'
import gql from 'graphql-tag'
import fetch from 'isomorphic-fetch'
import { getLocalTokens, removeAllItems, setLocalTokens } from '../localStorage'

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_AUTH_URI,
})

const cache = new InMemoryCache()

const typeDefs = gql`
  extend type Query {
    loggedIn: Boolean!
    userInfo: User!
  }
`

const authLink = setContext((_, { headers }) => {
  const [accessToken] = getLocalTokens()
  return {
    headers: {
      ...headers,
      authorization: accessToken ? `Bearer ${accessToken}` : '',
    },
  }
})

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward, response }) => {
    if (graphQLErrors) {
      if (graphQLErrors[0].extensions.code === 'UNAUTHENTICATED') {
        const [accessToken, refreshToken] = getLocalTokens()
        if (accessToken && refreshToken) {
          return new Observable((observer) => {
            fetch(process.env.REACT_APP_AUTH_URI, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                query: `
                mutation {
                  refreshTokens(refreshToken: "${refreshToken}") { 
                    token
                    refreshToken
                    user {
                      id
                      email 
                    }
                  }
                }`,
              }),
            })
              .then((res) => res.json())
              .then(({ data: { refreshTokens } }) => {
                setLocalTokens(refreshTokens.token, refreshTokens.refreshToken)

                operation.setContext(({ headers }) => {
                  return {
                    headers: {
                      ...headers,
                      authorization: `Bearer ${refreshTokens.token}` || null,
                    },
                  }
                })
              })
              .then(() => {
                const subscriber = {
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                }
                forward(operation).subscribe(subscriber)
              })
              .catch((error) => {
                console.log({ error })
                removeAllItems()
                operation.getContext().cache.writeQuery({
                  query: gql`
                    {
                      isLoggedIn
                    }
                  `,
                  data: { isLoggedIn: false },
                })
                observer.error(error)
              })
          })
        } else {
          response.errors = null
        }
      }
    }
    if (networkError) {
      console.log(`[Network error]: ${networkError}`)
    }
  }
)

export default new ApolloClient({
  link: from([authLink, errorLink, httpLink]),
  cache,
  fetch,
  typeDefs,
  resolvers: {
    Query: {
      isLoggedIn() {
        const [accessToken] = getLocalTokens()
        return !!accessToken
      },
    },
  },
})
